import { useState } from 'react';
import { showToast } from '@/components/base/Toast';
import { ApiError } from '@/api/client';
import { ZALO_PHONE_TAKEN, type ZaloRegisterPayload } from '@/api/auth';
import { ZaloMark } from './ZaloMark';

const fieldClass = (hasError: boolean) =>
  `w-full min-h-[52px] rounded-2xl border bg-slate-50 px-5 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-agrida-500 focus:bg-white focus:ring-2 focus:ring-agrida-500/20 ${hasError ? 'border-red-400 bg-red-50' : 'border-slate-200'}`;

/** Our own 409s carry a `code` next to the message; ApiError keeps the parsed body. */
function errorCode(err: ApiError): string | undefined {
  const body = err.body;
  return typeof body === 'object' && body !== null && 'code' in body
    ? String((body as { code?: unknown }).code)
    : undefined;
}

export interface ZaloSignupPending {
  signupToken: string;
  suggestedName?: string;
  pictureUrl?: string;
}

interface Props {
  pending: ZaloSignupPending;
  onSubmit: (payload: ZaloRegisterPayload) => Promise<void>;
  onCancel: () => void;
}

/**
 * Step 3 of Zalo sign-up. Zalo hands us a display name and nothing else usable —
 * the phone number sits behind a paid permission, and Agrida needs a role either
 * way — so a first-time Zalo user fills those in here. `signupToken` is the only
 * proof of identity carried forward; the Zalo id itself lives inside it,
 * server-signed, and is never posted from this form.
 */
export function ZaloCompleteProfile({ pending, onSubmit, onCancel }: Props) {
  const [role, setRole] = useState<'FARMER' | 'HTX'>('FARMER');
  const [fullName, setFullName] = useState(pending.suggestedName ?? '');
  const [phone, setPhone] = useState('');
  const [htxName, setHtxName] = useState('');
  const [htxCode, setHtxCode] = useState('');
  const [htxAddress, setHtxAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // The phone turned out to belong to an existing account: from here the form is
  // no longer creating anything, it is proving that account is the caller's so
  // Zalo can be attached to it and they can be let in.
  const [linkMode, setLinkMode] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  const clearError = (field: string) => setErrors((current) => ({ ...current, [field]: '' }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};

    if (!fullName.trim()) nextErrors.fullName = 'Vui lòng nhập họ tên';
    if (!phone.trim()) nextErrors.phone = 'Vui lòng nhập số điện thoại';
    else if (!/^\d{10}$/.test(phone)) nextErrors.phone = 'Số điện thoại phải có đúng 10 chữ số';
    if (linkMode && !password) nextErrors.password = 'Vui lòng nhập mật khẩu của tài khoản này';
    if (mfaRequired && !mfaCode.trim()) nextErrors.mfaCode = 'Vui lòng nhập mã xác thực 2 lớp';
    // Nothing is being created in link mode, so the cooperative's details are not
    // asked for — the existing account already has whatever it has.
    if (role === 'HTX' && !linkMode) {
      if (!htxName.trim()) nextErrors.htxName = 'Vui lòng nhập tên hợp tác xã';
      if (!htxCode.trim()) nextErrors.htxCode = 'Vui lòng nhập mã hợp tác xã';
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      showToast('Vui lòng kiểm tra lại thông tin', 'warning');
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      await onSubmit({
        signupToken: pending.signupToken,
        role,
        phone: phone.trim(),
        fullName: fullName.trim(),
        ...(linkMode ? { password, ...(mfaCode.trim() ? { mfaCode: mfaCode.trim() } : {}) } : {}),
        ...(role === 'HTX' && !linkMode
          ? { htxName: htxName.trim(), htxCode: htxCode.trim(), htxAddress: htxAddress.trim() || undefined }
          : {}),
      });
    } catch (err) {
      if (err instanceof ApiError && errorCode(err) === ZALO_PHONE_TAKEN) {
        // Not a failure the user has to recover from by starting over: switch the
        // form over to asking for that account's password and keep everything
        // they already typed.
        setLinkMode(true);
        setErrors({});
        showToast('Số điện thoại này đã có tài khoản — nhập mật khẩu để liên kết với Zalo', 'info');
      } else if (err instanceof ApiError && err.message.includes('MFA')) {
        setMfaRequired(true);
        showToast(err.message, 'warning');
      } else if (err instanceof ApiError) showToast(err.message, 'error');
      else if (err instanceof Error) showToast(err.message, 'error');
      else showToast('Không hoàn tất được đăng ký, vui lòng thử lại', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="mx-auto min-h-[100dvh] max-w-[540px] bg-white">
      <header className="grid h-[72px] grid-cols-[48px_1fr_48px] items-center border-b border-slate-100 px-5">
        <button
          type="button"
          onClick={onCancel}
          className="flex h-12 w-12 items-center justify-center rounded-xl text-slate-700 transition-colors hover:bg-slate-50"
          aria-label="Huỷ đăng ký bằng Zalo"
        >
          <i className="ri-arrow-left-line text-2xl" aria-hidden="true" />
        </button>
        <h1 className="text-center text-xl font-extrabold text-slate-950">
          {linkMode ? 'Liên kết tài khoản' : 'Hoàn tất đăng ký'}
        </h1>
      </header>

      <form onSubmit={handleSubmit} className="px-5 pb-8 pt-6 sm:px-8">
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          {pending.pictureUrl ? (
            <img src={pending.pictureUrl} alt="" className="h-11 w-11 rounded-full object-cover" />
          ) : (
            <ZaloMark className="h-11 w-11" />
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">{pending.suggestedName || 'Tài khoản Zalo'}</p>
            <p className="text-xs text-slate-500">
              {linkMode
                ? 'Đã xác thực qua Zalo — xác nhận mật khẩu để gắn Zalo vào tài khoản sẵn có.'
                : 'Đã xác thực qua Zalo — chỉ cần bổ sung vài thông tin nữa.'}
            </p>
          </div>
        </div>

        <fieldset className="mb-6" hidden={linkMode}>
          <legend className="mb-3 text-sm font-medium uppercase tracking-wide text-slate-500">Bạn là</legend>
          <div className="grid grid-cols-2 gap-3">
            {(
              [
                { value: 'FARMER', label: 'Nông hộ', icon: 'ri-plant-line' },
                { value: 'HTX', label: 'Hợp tác xã', icon: 'ri-community-line' },
              ] as const
            ).map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setRole(option.value)}
                aria-pressed={role === option.value}
                className={`flex min-h-[92px] flex-col items-center justify-center gap-2 rounded-2xl border-2 px-3 transition-colors ${
                  role === option.value
                    ? 'border-agrida-500 bg-agrida-50 text-agrida-700'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <i className={`${option.icon} text-2xl`} aria-hidden="true" />
                <span className="text-sm font-semibold">{option.label}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <div className="space-y-4">
          <div hidden={linkMode}>
            <label htmlFor="zalo-fullname" className="mb-1.5 block text-sm font-medium text-slate-700">
              {role === 'HTX' ? 'Họ tên người đại diện' : 'Họ tên'}
            </label>
            <input
              id="zalo-fullname"
              value={fullName}
              onChange={(event) => { setFullName(event.target.value); clearError('fullName'); }}
              className={fieldClass(Boolean(errors.fullName))}
              placeholder="Nhập họ tên"
            />
            {errors.fullName && <p className="mt-1 text-[11px] text-red-500">{errors.fullName}</p>}
          </div>

          <div>
            <label htmlFor="zalo-phone" className="mb-1.5 block text-sm font-medium text-slate-700">Số điện thoại</label>
            <input
              id="zalo-phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              value={phone}
              readOnly={linkMode}
              onChange={(event) => { setPhone(event.target.value.replace(/\D/g, '')); clearError('phone'); }}
              className={`${fieldClass(Boolean(errors.phone))}${linkMode ? ' bg-slate-100 text-slate-500' : ''}`}
              placeholder="Nhập số điện thoại"
            />
            {errors.phone ? (
              <p className="mt-1 text-[11px] text-red-500">{errors.phone}</p>
            ) : linkMode ? (
              <button
                type="button"
                onClick={() => { setLinkMode(false); setPassword(''); setMfaRequired(false); setMfaCode(''); }}
                className="mt-1 text-[11px] font-medium text-agrida-700 underline"
              >
                Dùng số điện thoại khác
              </button>
            ) : (
              <p className="mt-1 text-[11px] text-slate-500">
                Đây là số điện thoại cho tài khoản Agrida. Nếu số này đã có tài khoản, bạn chỉ cần
                nhập mật khẩu của nó ở bước sau để đăng nhập bằng Zalo từ lần sau.
              </p>
            )}
          </div>

          {linkMode && (
            <div>
              <label htmlFor="zalo-password" className="mb-1.5 block text-sm font-medium text-slate-700">
                Mật khẩu của tài khoản {phone}
              </label>
              <div className="relative">
                <input
                  id="zalo-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => { setPassword(event.target.value); clearError('password'); }}
                  className={`${fieldClass(Boolean(errors.password))} pr-14`}
                  placeholder="Nhập mật khẩu"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute inset-y-0 right-0 flex w-14 items-center justify-center text-slate-400"
                  aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                >
                  <i className={showPassword ? 'ri-eye-off-line text-xl' : 'ri-eye-line text-xl'} aria-hidden="true" />
                </button>
              </div>
              {errors.password ? (
                <p className="mt-1 text-[11px] text-red-500">{errors.password}</p>
              ) : (
                <p className="mt-1 text-[11px] text-slate-500">
                  Chỉ cần nhập một lần. Sau khi liên kết, bấm “Đăng nhập bằng Zalo” là vào thẳng.
                </p>
              )}
            </div>
          )}

          {mfaRequired && (
            <div>
              <label htmlFor="zalo-mfa" className="mb-1.5 block text-sm font-medium text-slate-700">
                Mã xác thực 2 lớp
              </label>
              <input
                id="zalo-mfa"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={(event) => { setMfaCode(event.target.value.replace(/\D/g, '')); clearError('mfaCode'); }}
                className={fieldClass(Boolean(errors.mfaCode))}
                placeholder="6 chữ số trong ứng dụng xác thực"
              />
              {errors.mfaCode && <p className="mt-1 text-[11px] text-red-500">{errors.mfaCode}</p>}
            </div>
          )}

          {role === 'HTX' && !linkMode && (
            <>
              <div>
                <label htmlFor="zalo-htx-name" className="mb-1.5 block text-sm font-medium text-slate-700">Tên hợp tác xã</label>
                <input
                  id="zalo-htx-name"
                  value={htxName}
                  onChange={(event) => { setHtxName(event.target.value); clearError('htxName'); }}
                  className={fieldClass(Boolean(errors.htxName))}
                  placeholder="VD: HTX Nông nghiệp Hòa Phát"
                />
                {errors.htxName && <p className="mt-1 text-[11px] text-red-500">{errors.htxName}</p>}
              </div>
              <div>
                <label htmlFor="zalo-htx-code" className="mb-1.5 block text-sm font-medium text-slate-700">Mã hợp tác xã</label>
                <input
                  id="zalo-htx-code"
                  value={htxCode}
                  onChange={(event) => { setHtxCode(event.target.value); clearError('htxCode'); }}
                  className={fieldClass(Boolean(errors.htxCode))}
                  placeholder="VD: HTX-HP-2025"
                />
                {errors.htxCode && <p className="mt-1 text-[11px] text-red-500">{errors.htxCode}</p>}
              </div>
              <div>
                <label htmlFor="zalo-htx-address" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Địa chỉ <span className="font-normal text-slate-400">(không bắt buộc)</span>
                </label>
                <input
                  id="zalo-htx-address"
                  value={htxAddress}
                  onChange={(event) => setHtxAddress(event.target.value)}
                  className={fieldClass(false)}
                  placeholder="Nhập địa chỉ hợp tác xã"
                />
              </div>
            </>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-7 min-h-[48px] w-full rounded-xl bg-agrida-600 px-4 font-semibold text-white transition-colors hover:bg-agrida-700 disabled:opacity-60"
        >
          {loading
            ? linkMode
              ? 'Đang liên kết...'
              : 'Đang tạo tài khoản...'
            : linkMode
              ? 'Liên kết và đăng nhập'
              : 'Hoàn tất đăng ký'}
        </button>
      </form>
    </main>
  );
}
